import React, { useState, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CloseIcon from '@mui/icons-material/Close';
import BackToTop from './BackToTop';

const QUERIES_PER_PAGE = 10;
const ANSWER_PREVIEW_CHARS = 600;

// Color band for the 0-10 assessment rating, mirroring PostV2's rating pill
// (high = green promoter, med = amber passive, low = red detractor).
function ratingClass(rating) {
    if (rating === null || rating === undefined) return 'med';
    if (rating > 8) return 'high';   // promoter (9-10)
    if (rating > 6) return 'med';    // passive (7-8)
    return 'low';                     // detractor (0-6)
}

// brand_mentions -> readable chip label
function brandLabel(value) {
    if (!value) return null;
    return String(value).replace(/_/g, ' ');
}

// question_type -> what kind of prompt produced this answer. 'open' prompts name
// no brand, so they are the ones where failing to appear means something.
const QUESTION_TYPE_LABELS = {
    open: 'open category prompt',
    brand: 'brand-specific prompt',
    h2h: 'head-to-head prompt',
};

const sortFunctions = {
    ratingHighToLow: (a, b) => (b.rating ?? -Infinity) - (a.rating ?? -Infinity),
    ratingLowToHigh: (a, b) => (a.rating ?? Infinity) - (b.rating ?? Infinity),
    newest: (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0),
    oldest: (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
};

// Render markdown safely (GFM tables/lists, no raw HTML). Links open in a new
// tab; images are constrained so ChatGPT ad graphics can't blow out the card.
const markdownComponents = {
    a: ({ node, children, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer">{children}</a>,
    img: ({ node, ...props }) => (
        <img {...props} style={{ maxWidth: '100%', height: 'auto', borderRadius: 6 }} alt={props.alt || ''} />
    ),
    table: ({ node, ...props }) => (
        <div className="llm-answer-table-wrap"><table {...props} /></div>
    ),
};

function QueryCard({ query, statName }) {
    const [expanded, setExpanded] = useState(false);
    const [showDetails, setShowDetails] = useState(false);

    const answer = query.answer_text_markdown || '';
    const isLong = answer.length > ANSWER_PREVIEW_CHARS;
    const shown = expanded || !isLong ? answer : `${answer.slice(0, ANSWER_PREVIEW_CHARS)}…`;

    const date = query.created_at ? new Date(query.created_at) : null;
    const dateLabel = date && !isNaN(date)
        ? date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
        : null;
    const brand = brandLabel(query.brand_mentions);
    const hasRating = query.rating !== null && query.rating !== undefined;

    return (
        <div className="llm-query-card">
            {hasRating && (
                <div className="llm-rating-container">
                    <span
                        className={`llm-rating ${ratingClass(query.rating)}`}
                        onClick={() => setShowDetails(s => !s)}
                    >
                        {statName}: {query.rating} / 10
                    </span>
                    {showDetails && query.explanation && (
                        <p className="llm-rating-explanation">{query.explanation}</p>
                    )}
                </div>
            )}

            <div className="llm-query-header">
                {dateLabel && <span className="llm-query-time"><em>{dateLabel}</em></span>}
                <div className="llm-query-meta">
                    {/* Which brand this row scores. Every row in a drill-down belongs
                        to the selected brand — showing it makes that checkable. */}
                    {query.topic && <span className="llm-chip">{query.topic}</span>}
                    {brand && <span className="llm-chip">{brand}</span>}
                    {query.list_position != null && (
                        <span className="llm-chip">Position: {query.list_position}</span>
                    )}
                    {(query.confidence !== null && query.confidence !== undefined) && (
                        <span className="llm-chip subtle">Confidence: {query.confidence}/10</span>
                    )}
                    {query.question_type && (
                        <span className="llm-chip subtle">
                            {QUESTION_TYPE_LABELS[query.question_type] || query.question_type}
                        </span>
                    )}
                    {/* The answer named something like "VIFP Club" and the pipeline
                        rolled it up to the parent brand — worth flagging. */}
                    {query.rolled_up && query.extracted_name && (
                        <span className="llm-chip subtle">rolled up from “{query.extracted_name}”</span>
                    )}
                    {(query.model || query.source) && (
                        <span className="llm-chip subtle">{query.model || query.source}</span>
                    )}
                    {query.web_search_triggered && <span className="llm-chip subtle">web search</span>}
                </div>
            </div>

            <div className="llm-query-body">
                <p className="llm-query-title">{query.prompt || '(no prompt text)'}</p>
                {answer && (
                    <div className="llm-query-answer">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                            {shown}
                        </ReactMarkdown>
                        {isLong && (
                            <button className="llm-read-more" onClick={() => setExpanded(e => !e)}>
                                {expanded ? 'Show less' : 'Read more'}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * LLMQueriesDisplay — drill-down list for the LLM Recommendations chart.
 * Renders the prompts + ChatGPT answers (and their assessments) behind a
 * clicked point/period. Mirrors FilteredPostsDisplay's shell + PostV2's card
 * look (rating pill, muted date, title, read-more) but renders query/answer
 * content instead of posts.
 */
const LLMQueriesDisplay = ({
    queries = [],
    onClose,
    title = 'Prompts & ChatGPT Responses',
    id = 'llm-queries',
    autoScroll = false,
    statName = 'Rec',
}) => {
    const [displayCount, setDisplayCount] = useState(QUERIES_PER_PAGE);
    const [sortMethod, setSortMethod] = useState('ratingHighToLow');

    useEffect(() => {
        if (autoScroll && queries.length > 0) {
            const section = document.getElementById(id);
            const mainContent = document.querySelector('.company-main-content');
            if (section && mainContent) {
                mainContent.scrollTo({ top: section.offsetTop - 20, behavior: 'smooth' });
            }
        }
    }, [queries, id, autoScroll]);

    const sorted = useMemo(
        () => [...queries].sort(sortFunctions[sortMethod] || sortFunctions.ratingHighToLow),
        [queries, sortMethod]
    );

    if (queries.length === 0) return null;

    const displayed = sorted.slice(0, displayCount);
    const hasMore = sorted.length > displayCount;

    return (
        <div id={id} className="llm-queries-section">
            {onClose && (
                <div className="llm-queries-close">
                    <button onClick={onClose} className="llm-queries-close-button" aria-label="Close">
                        <CloseIcon />
                    </button>
                </div>
            )}
            <div className="llm-queries-header">
                <h3 className="llm-queries-title">{title}</h3>
                {sorted.length > 1 && (
                    <select
                        className="llm-queries-sort"
                        value={sortMethod}
                        onChange={(e) => setSortMethod(e.target.value)}
                    >
                        <option value="ratingHighToLow">{statName} (High to Low)</option>
                        <option value="ratingLowToHigh">{statName} (Low to High)</option>
                        <option value="newest">Newest First</option>
                        <option value="oldest">Oldest First</option>
                    </select>
                )}
            </div>

            <div className="llm-query-list">
                {displayed.map((q, idx) => (
                    <QueryCard key={q.id ? `${q.id}-${q.topic_id ?? ''}` : idx} query={q} statName={statName} />
                ))}
            </div>

            {hasMore && (
                <div className="llm-view-more-container">
                    <button
                        className="llm-view-more-button"
                        onClick={() => setDisplayCount(prev => prev + QUERIES_PER_PAGE)}
                    >
                        View More
                    </button>
                </div>
            )}
            <BackToTop />
        </div>
    );
};

export default LLMQueriesDisplay;
